import { Add as AddIcon, Delete as DeleteIcon, PhotoCamera } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { storage } from '../firebase';
import {
  FoodResponse,
  calculateNutritionFromFood,
  searchFoods,
} from '../services/foodsApi';
import { CreateMealRequest, Meal } from '../services/mealApi';
import CancelButton from './CancelButton';

const UNIT_OPTIONS = [
  { value: 'gram', label: 'Gram' },
  { value: 'porsiyon', label: 'Porsiyon' },
  { value: 'adet', label: 'Adet' },
  { value: 'ml', label: 'ML' },
  { value: 'su bardağı', label: 'Su Bardağı' },
  { value: 'dilim', label: 'Dilim' },
  { value: 'kase', label: 'Kase' },
  { value: 'fincan', label: 'Fincan' },
  { value: 'yemek kaşığı', label: 'Yemek Kaşığı' },
  { value: 'salkım', label: 'Salkım' },
];

export interface MealItemRow {
  id: string;
  food: FoodResponse | null;
  foodName: string;
  quantity: number;
  unit: string;
  calories: number;
  protein: number;
  carbohydrates: number;
  fat: number;
  fiber: number;
}

interface MealEntryModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (mealData: CreateMealRequest) => Promise<void>;
  clientId: string;
  clientName: string;
  selectedDate: Date;
  initialData?: Meal;
}

function createEmptyMealItem(): MealItemRow {
  return {
    id: `item-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    food: null,
    foodName: '',
    quantity: 0,
    unit: 'gram',
    calories: 0,
    protein: 0,
    carbohydrates: 0,
    fat: 0,
    fiber: 0,
  };
}

const MealEntryModal: React.FC<MealEntryModalProps> = ({
  open,
  onClose,
  onSave,
  clientId,
  clientName,
  selectedDate,
  initialData,
}) => {
  const mealDateStr = selectedDate.toISOString().split('T')[0];

  // Tek öğün formu (diyetisyen veya düzenleme)
  const [formData, setFormData] = useState<CreateMealRequest>({
    clientId,
    clientName,
    mealDate: mealDateStr,
    mealType: '',
    foodName: '',
    quantity: undefined,
    unit: 'gram',
    calories: 0,
    protein: 0,
    carbohydrates: 0,
    fat: 0,
    fiber: 0,
    notes: '',
    photoUrl: undefined,
  });

  // Danışan çoklu yemek listesi (sadece yeni öğün eklerken)
  const [mealItems, setMealItems] = useState<MealItemRow[]>(() => [
    createEmptyMealItem(),
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [foodOptions, setFoodOptions] = useState<FoodResponse[]>([]);
  const [selectedFood, setSelectedFood] = useState<FoodResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const userRole = localStorage.getItem('userRole') || 'client';
  const isDietitian = userRole === 'dietitian';
  // Yeni öğün eklerken (düzenleme değilse) hem danışan hem diyetisyen birden fazla besin ekleyebilir
  const isMultiFoodMode = !initialData;

  const handleInputChange =
    (field: keyof CreateMealRequest) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> & { target: { type?: string; value: unknown } }) => {
      const target = event.target;
      const value =
        target.type === 'number'
          ? parseFloat(String(target.value)) || 0
          : target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, photo: 'Lütfen bir resim dosyası seçin' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        photo: "Dosya boyutu 5MB'dan küçük olmalıdır",
      }));
      return;
    }
    try {
      setUploading(true);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.photo;
        return next;
      });
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
      const fileName = `meals/${clientId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setFormData((prev) => ({ ...prev, photoUrl: downloadURL }));
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrors((prev) => ({ ...prev, photo: 'Resim yüklenirken hata oluştu' }));
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    setPreviewUrl(null);
    setFormData((prev) => ({ ...prev, photoUrl: undefined }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Autocomplete için arama (debounced)
  const fetchFoodOptions = useCallback(async (query: string) => {
    if (!query || query.trim().length < 2) {
      setFoodOptions([]);
      return;
    }
    try {
      setSearching(true);
      const results = await searchFoods(query.trim());
      setFoodOptions(results.slice(0, 12));
    } catch (error) {
      console.error('Error searching foods:', error);
      setFoodOptions([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const onAutocompleteInputChange = useCallback(
    (_: unknown, value: string) => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      setFormData((prev) => ({ ...prev, foodName: value }));
      if (!value.trim()) {
        setSelectedFood(null);
        setFoodOptions([]);
        return;
      }
      searchTimeoutRef.current = setTimeout(() => {
        fetchFoodOptions(value);
      }, 350);
    },
    [fetchFoodOptions]
  );

  const onAutocompleteChange = useCallback(
    (_: unknown, food: FoodResponse | null) => {
      setSelectedFood(food);
      if (!food) {
        setFormData((prev) => ({
          ...prev,
          foodName: '',
          quantity: undefined,
          unit: 'gram',
          calories: 0,
          protein: 0,
          carbohydrates: 0,
          fat: 0,
          fiber: 0,
        }));
        return;
      }
      const qty = formData.quantity && formData.quantity > 0 ? formData.quantity : 1;
      const unit = food.standardUnit && food.standardUnitWeight ? food.standardUnit : 'gram';
      setFormData((prev) => ({
        ...prev,
        foodName: food.name,
        quantity: qty,
        unit,
      }));
      const nutrition = calculateNutritionFromFood(food, qty, unit);
      setFormData((prev) => ({
        ...prev,
        calories: nutrition.calories,
        protein: nutrition.protein,
        carbohydrates: nutrition.carbohydrates,
        fat: nutrition.fat,
        fiber: nutrition.fiber,
      }));
    },
    [formData.quantity]
  );

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // Miktar/birim değişince besin varsa yeniden hesapla (tek form)
  useEffect(() => {
    if (selectedFood && formData.quantity != null && formData.quantity > 0 && formData.unit) {
      const nutrition = calculateNutritionFromFood(
        selectedFood,
        formData.quantity,
        formData.unit
      );
      setFormData((prev) => ({
        ...prev,
        calories: nutrition.calories,
        protein: nutrition.protein,
        carbohydrates: nutrition.carbohydrates,
        fat: nutrition.fat,
        fiber: nutrition.fiber,
      }));
    }
  }, [formData.quantity, formData.unit, selectedFood]);

  // ----- Çoklu yemek (danışan) -----
  const addMealItem = useCallback(() => {
    setMealItems((prev) => [...prev, createEmptyMealItem()]);
  }, []);

  const removeMealItem = useCallback((id: string) => {
    setMealItems((prev) => {
      const next = prev.filter((i) => i.id !== id);
      return next.length > 0 ? next : [createEmptyMealItem()];
    });
  }, []);

  const updateMealItem = useCallback((id: string, patch: Partial<MealItemRow>) => {
    setMealItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, ...patch } : i))
    );
  }, []);

  const setMealItemFood = useCallback(
    (id: string, food: FoodResponse | null) => {
      const row = mealItems.find((i) => i.id === id);
      if (!row) return;
      const foodName = food ? food.name : '';
      const qty = row.quantity > 0 ? row.quantity : 1;
      const unit =
        food?.standardUnit && food?.standardUnitWeight ? food.standardUnit : 'gram';
      const nutrition = food
        ? calculateNutritionFromFood(food, qty, unit)
        : { calories: 0, protein: 0, carbohydrates: 0, fat: 0, fiber: 0 };
      updateMealItem(id, {
        food,
        foodName,
        quantity: qty,
        unit,
        ...nutrition,
      });
    },
    [mealItems, updateMealItem]
  );

  const setMealItemQuantityUnit = useCallback(
    (id: string, quantity: number, unit: string) => {
      const row = mealItems.find((i) => i.id === id);
      if (!row?.food) {
        updateMealItem(id, { quantity, unit });
        return;
      }
      const nutrition = calculateNutritionFromFood(row.food, quantity, unit);
      updateMealItem(id, { quantity, unit, ...nutrition });
    },
    [mealItems, updateMealItem]
  );

  // Modal açıldığında state sıfırla / initialData doldur
  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setFormData({
        clientId: initialData.clientId,
        clientName: initialData.clientName,
        mealDate: initialData.mealDate,
        mealType: initialData.mealType,
        foodName: initialData.foodName,
        quantity: initialData.quantity,
        unit: initialData.unit ?? 'gram',
        calories: initialData.calories,
        protein: initialData.protein,
        carbohydrates: initialData.carbohydrates,
        fat: initialData.fat,
        fiber: initialData.fiber,
        notes: initialData.notes,
        photoUrl: initialData.photoUrl,
      });
      setSelectedFood(null);
      setPreviewUrl(initialData.photoUrl ?? null);
    } else if (isMultiFoodMode) {
      setMealItems([createEmptyMealItem()]);
      setFormData({
        clientId,
        clientName,
        mealDate: mealDateStr,
        mealType: '',
        foodName: '',
        quantity: undefined,
        unit: 'gram',
        calories: 0,
        protein: 0,
        carbohydrates: 0,
        fat: 0,
        fiber: 0,
        notes: '',
        photoUrl: undefined,
      });
      setPreviewUrl(null);
    } else {
      setFormData({
        clientId,
        clientName,
        mealDate: mealDateStr,
        mealType: '',
        foodName: '',
        quantity: undefined,
        unit: 'gram',
        calories: 0,
        protein: 0,
        carbohydrates: 0,
        fat: 0,
        fiber: 0,
        notes: '',
        photoUrl: undefined,
      });
      setSelectedFood(null);
      setPreviewUrl(null);
    }
    setErrors({});
    setFoodOptions([]);
  }, [open, initialData, isMultiFoodMode, clientId, clientName, mealDateStr]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.mealType.trim()) newErrors.mealType = 'Öğün türü seçiniz';

    if (isMultiFoodMode) {
      const hasValidItem = mealItems.some(
        (i) => i.foodName.trim() && i.quantity > 0 && i.calories >= 0
      );
      if (!hasValidItem)
        newErrors.items = 'En az bir yemek seçin ve miktar girin';
    } else {
      if (!formData.foodName.trim()) newErrors.foodName = 'Yemek adı gereklidir';
      if (formData.calories < 0) newErrors.calories = 'Kalori negatif olamaz';
      if (formData.protein < 0) newErrors.protein = 'Protein negatif olamaz';
      if (formData.carbohydrates < 0)
        newErrors.carbohydrates = 'Karbonhidrat negatif olamaz';
      if (formData.fat < 0) newErrors.fat = 'Yağ negatif olamaz';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      if (isMultiFoodMode) {
        const validItems = mealItems.filter(
          (i) => i.foodName.trim() && i.quantity > 0
        );
        if (validItems.length === 0) return;
        const notes = formData.notes || '';
        const photoUrl = formData.photoUrl;
        for (let idx = 0; idx < validItems.length; idx++) {
          const item = validItems[idx];
          await onSave({
            clientId,
            clientName,
            mealDate: mealDateStr,
            mealType: formData.mealType,
            foodName: item.foodName,
            quantity: item.quantity,
            unit: item.unit,
            calories: item.calories,
            protein: item.protein,
            carbohydrates: item.carbohydrates,
            fat: item.fat,
            fiber: item.fiber,
            notes: idx === 0 ? notes : undefined,
            photoUrl: idx === 0 ? photoUrl : undefined,
          });
        }
      } else {
        await onSave(formData);
      }
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error saving meal:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      clientId,
      clientName,
      mealDate: mealDateStr,
      mealType: '',
      foodName: '',
      quantity: undefined,
      unit: 'gram',
      calories: 0,
      protein: 0,
      carbohydrates: 0,
      fat: 0,
      fiber: 0,
      notes: '',
      photoUrl: undefined,
    });
    setMealItems([createEmptyMealItem()]);
    setErrors({});
    setPreviewUrl(null);
    setSelectedFood(null);
    setFoodOptions([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const totalFromItems = {
    calories: mealItems.reduce((s, i) => s + i.calories, 0),
    protein: mealItems.reduce((s, i) => s + i.protein, 0),
    carbohydrates: mealItems.reduce((s, i) => s + i.carbohydrates, 0),
    fat: mealItems.reduce((s, i) => s + i.fat, 0),
    fiber: mealItems.reduce((s, i) => s + i.fiber, 0),
  };

  const renderFoodAutocomplete = (
    value: FoodResponse | null,
    onChange: (food: FoodResponse | null) => void,
    options: FoodResponse[],
    label: string,
    error?: string,
    helperText?: string,
    inputValue?: string,
    onInputChangeCustom?: (value: string) => void
  ) => (
    <Autocomplete
      fullWidth
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue !== undefined ? inputValue : (value ? value.name : '')}
      onInputChange={(_, newInputValue) => {
        if (onInputChangeCustom) {
          onInputChangeCustom(newInputValue);
          if (!newInputValue) onChange(null);
        } else if (!newInputValue) onChange(null);
      }}
      options={options}
      getOptionLabel={(option) => option.name}
      filterOptions={(x) => x}
      loading={searching}
      onOpen={() => {
        if (value?.name && value.name.length >= 2) fetchFoodOptions(value.name);
      }}
      onClose={() => setFoodOptions([])}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!error}
          helperText={error || helperText}
          placeholder="Yemek ara veya seçin..."
          required
          onKeyDown={(e) => {
            if ((e.key === 'Backspace' || e.key === 'Delete') && value) {
              e.preventDefault();
              onChange(null);
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {searching ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.category} • {option.calories} kcal/100g • P: {option.protein}g
              K: {option.carbohydrates}g Y: {option.fat}g
            </Typography>
          </Box>
        </li>
      )}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
    />
  );

  const renderSingleFoodSection = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
      <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
        {renderFoodAutocomplete(
          selectedFood,
          (food) => onAutocompleteChange(null, food),
          foodOptions,
          'Yemek Adı',
          errors.foodName,
          'Yazmaya başlayın, listeden seçin',
          selectedFood ? selectedFood.name : formData.foodName,
          (value) => onAutocompleteInputChange(null, value)
        )}
      </Box>
      <Box sx={{ flex: '0 0 120px' }}>
        <TextField
          fullWidth
          label="Miktar"
          type="number"
          value={formData.quantity ?? ''}
          onChange={(e) => {
            const v = parseFloat(e.target.value) || 0;
            setFormData((prev) => ({ ...prev, quantity: v }));
            if (selectedFood && formData.unit)
              setFormData((prev) => ({
                ...prev,
                ...calculateNutritionFromFood(selectedFood, v, formData.unit),
              }));
          }}
          inputProps={{ min: 0, step: 0.1 }}
          placeholder="Örn: 200"
        />
      </Box>
      <Box sx={{ flex: '0 0 140px' }}>
        <FormControl fullWidth size="medium">
          <InputLabel>Birim</InputLabel>
          <Select
            value={formData.unit}
            label="Birim"
            onChange={(e) => {
              const u = e.target.value as string;
              setFormData((prev) => ({ ...prev, unit: u }));
              if (selectedFood && (formData.quantity ?? 0) > 0)
                setFormData((prev) => ({
                  ...prev,
                  ...calculateNutritionFromFood(
                    selectedFood,
                    formData.quantity ?? 0,
                    u
                  ),
                }));
            }}
          >
            {selectedFood?.standardUnit && selectedFood?.standardUnitWeight && (
              <MenuItem value={selectedFood.standardUnit}>
                {selectedFood.standardUnit} ({selectedFood.standardUnitWeight}g) ⭐
              </MenuItem>
            )}
            {UNIT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  const renderMealItemsSection = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Bu öğüne eklenen yemekler (birden fazla ekleyebilirsiniz)
      </Typography>
      {mealItems.map((row) => (
        <Box
          key={row.id}
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
              <Autocomplete
                fullWidth
                value={row.food}
                onChange={(_, newValue) => setMealItemFood(row.id, newValue)}
                inputValue={row.food ? row.food.name : row.foodName}
                onInputChange={(_, newInputValue) => {
                  if (!newInputValue) {
                    setMealItemFood(row.id, null);
                  } else {
                    updateMealItem(row.id, { foodName: newInputValue });
                  }
                  if (newInputValue.trim().length >= 2) {
                    fetchFoodOptions(newInputValue);
                  } else {
                    setFoodOptions([]);
                  }
                }}
                options={foodOptions}
                getOptionLabel={(option) => option.name}
                filterOptions={(x) => x}
                loading={searching}
                onOpen={() => {
                  if (row.foodName && row.foodName.length >= 2)
                    fetchFoodOptions(row.foodName);
                }}
                onClose={() => setFoodOptions([])}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Yemek"
                    placeholder="Ara ve seçin..."
                    size="small"
                    onKeyDown={(e) => {
                      if ((e.key === 'Backspace' || e.key === 'Delete') && row.food) {
                        e.preventDefault();
                        setMealItemFood(row.id, null);
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searching ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Typography variant="body2" fontWeight={600}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {option.calories} kcal/100g
                    </Typography>
                  </li>
                )}
                isOptionEqualToValue={(option, val) => option.id === val?.id}
              />
            </Box>
            <TextField
              size="small"
              label="Miktar"
              type="number"
              value={row.quantity || ''}
              onChange={(e) => {
                const q = parseFloat(e.target.value) || 0;
                setMealItemQuantityUnit(row.id, q, row.unit);
              }}
              inputProps={{ min: 0, step: 0.1 }}
              sx={{ width: 100 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Birim</InputLabel>
              <Select
                value={row.unit}
                label="Birim"
                onChange={(e) =>
                  setMealItemQuantityUnit(
                    row.id,
                    row.quantity || 0,
                    e.target.value as string
                  )
                }
              >
                {row.food?.standardUnit && row.food?.standardUnitWeight && (
                  <MenuItem value={row.food.standardUnit}>
                    {row.food.standardUnit} ({row.food.standardUnitWeight}g)
                  </MenuItem>
                )}
                {UNIT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {row.calories} kcal
              </Typography>
              <IconButton
                size="small"
                sx={{width:'fit-content'}}
                color="error"
                onClick={() => removeMealItem(row.id)}
                aria-label="Yemek satırını kaldır"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      ))}
      {errors.items && (
        <Typography variant="caption" color="error">
          {errors.items}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
        <IconButton
          onClick={addMealItem}
          size="medium"
          sx={{
            border: '2px dashed',
            width: 'fit-content',
            borderColor: '#43A047',
            color: '#43A047',
            '&:hover': { bgcolor: 'rgba(67, 160, 71, 0.08)', borderColor: '#388E3C', color: '#388E3C' },
          }}
          aria-label="Bir yemek daha ekle"
        >
          <AddIcon />
        </IconButton>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Bir yemek daha ekle
        </Typography>
      </Box>
    </Box>
  );

  const renderNutritionCards = (
    cal: number,
    pro: number,
    carb: number,
    fat: number,
    fib: number
  ): React.ReactElement => {
    const items = [
      { key: 'Kalori', value: cal, unit: 'kcal', color: '#e8f5e9', border: '#c8e6c9' },
      { key: 'Protein', value: pro, unit: 'g', color: '#e3f2fd', border: '#bbdefb' },
      { key: 'Karbonhidrat', value: carb, unit: 'g', color: '#fff3e0', border: '#ffe0b2' },
      { key: 'Yağ', value: fat, unit: 'g', color: '#f3e5f5', border: '#e1bee7' },
      { key: 'Lif', value: fib, unit: 'g', color: '#fce4ec', border: '#f8bbd0' },
    ];
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 2,
        }}
      >
        {items.map(({ key, value, unit, color, border }) => (
          <Box
            key={key}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: color,
              border: '1px solid',
              borderColor: border,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              {key}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {value.toFixed(key === 'Kalori' ? 0 : 1)}
              <Typography component="span" variant="body2" sx={{ ml: 0.5 }}>
                {unit}
              </Typography>
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div">
          {initialData ? 'Öğün Düzenle' : 'Yeni Öğün Ekle'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {new Date(selectedDate).toLocaleDateString('tr-TR')} - {clientName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth required error={!!errors.mealType} sx={{ maxWidth: 320 }}>
              <InputLabel>Öğün Türü</InputLabel>
              <Select
                value={formData.mealType}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, mealType: e.target.value as string }))
                }
                label="Öğün Türü"
              >
                <MenuItem value="Kahvaltı">Kahvaltı</MenuItem>
                <MenuItem value="Ara Öğün">Ara Öğün</MenuItem>
                <MenuItem value="Öğle Yemeği">Öğle Yemeği</MenuItem>
                <MenuItem value="Akşam Yemeği">Akşam Yemeği</MenuItem>
                <MenuItem value="Gece Atıştırması">Gece Atıştırması</MenuItem>
              </Select>
              {errors.mealType && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.mealType}
                </Typography>
              )}
            </FormControl>
          </Box>

          {isMultiFoodMode ? (
            <>
              {renderMealItemsSection()}
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                📊 Öğün toplam besin değerleri
              </Typography>
              {renderNutritionCards(
                totalFromItems.calories,
                totalFromItems.protein,
                totalFromItems.carbohydrates,
                totalFromItems.fat,
                totalFromItems.fiber
              )}
            </>
          ) : (
            <>
              {renderSingleFoodSection()}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                Besin Değerleri
              </Typography>
              {!isDietitian ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Otomatik hesaplanıyor
                  </Typography>
                  {renderNutritionCards(
                    formData.calories,
                    formData.protein,
                    formData.carbohydrates,
                    formData.fat,
                    formData.fiber
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                  {[
                    { key: 'calories', label: 'Kalori (kcal)', min: 0 },
                    { key: 'protein', label: 'Protein (g)', min: 0 },
                    { key: 'carbohydrates', label: 'Karbonhidrat (g)', min: 0 },
                    { key: 'fat', label: 'Yağ (g)', min: 0 },
                    { key: 'fiber', label: 'Lif (g)', min: 0 },
                  ].map(({ key, label, min }) => (
                    <TextField
                      key={key}
                      fullWidth
                      sx={{ flex: '1 1 160px', minWidth: '160px' }}
                      label={label}
                      type="number"
                      value={formData[key as keyof CreateMealRequest] ?? ''}
                      onChange={handleInputChange(key as keyof CreateMealRequest)}
                      error={!!errors[key]}
                      helperText={errors[key]}
                      inputProps={{ min, step: 0.1 }}
                    />
                  ))}
                </Box>
              )}
            </>
          )}

          <Box sx={{ mb: 3 ,mt:3}}>
            <TextField
              fullWidth
              label="Notlar"
              value={formData.notes || ''}
              onChange={handleInputChange('notes')}
              multiline
              rows={2}
              placeholder="Öğün hakkında notlar..."
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Öğün Fotoğrafı
            </Typography>
            <input
              ref={fileInputRef}
              accept="image/*"
              style={{ display: 'none' }}
              id="photo-upload"
              type="file"
              onChange={handleFileSelect}
            />
            {previewUrl ? (
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <img
                  src={previewUrl}
                  alt="Öğün önizleme"
                  style={{
                    display: 'block',
                    width: '100%',
                    maxWidth: 280,
                    maxHeight: 280,
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    p: 1.5,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={handleRemovePhoto}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.9)',
                      color: 'error.main',
                      '&:hover': { bgcolor: 'white' },
                    }}
                    aria-label="Fotoğrafı kaldır"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ) : (
              <label htmlFor="photo-upload" style={{ display: 'block', cursor: uploading ? 'wait' : 'pointer' }}>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: uploading ? 'action.disabled' : '#43A047',
                    borderRadius: 3,
                    p: 4,
                    textAlign: 'center',
                    bgcolor: uploading ? 'action.hover' : 'transparent',
                    transition: 'background-color 0.2s, border-color 0.2s',
                    '&:hover': uploading
                      ? {}
                      : {
                          bgcolor: 'rgba(67, 160, 71, 0.08)',
                          borderColor: '#388E3C',
                        },
                  }}
                >
                  {uploading ? (
                    <CircularProgress size={40} sx={{ color: '#43A047', mb: 1 }} />
                  ) : (
                    <PhotoCamera sx={{ fontSize: 48, color: '#43A047', mb: 1, opacity: 0.8 }} />
                  )}
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {uploading ? 'Yükleniyor...' : 'Tıklayın veya sürükleyip bırakın'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    PNG, JPG • En fazla 5MB
                  </Typography>
                </Box>
              </label>
            )}
            {errors.photo && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors.photo}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <CancelButton onClick={handleClose} />
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={uploading}
          sx={{
            bgcolor: '#43A047',
            '&:hover': { bgcolor: '#388E3C' },
          }}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MealEntryModal;
